import type { DataBundle, HistoryEntry, ImageRecord, Row } from '../types'

interface ResponseData {
  ok: boolean
  message?: string
  token?: string
  students?: Row[]
  sessions?: Row[]
  files?: Record<string, Row[]>
  history?: HistoryEntry[]
  images?: ImageRecord[]
  metadata?: Row[]
  code?: string
  configured?: boolean
  commitSha?: string
  commitUrl?: string
}

async function parse(response: Response): Promise<ResponseData> {
  try { return await response.json() as ResponseData }
  catch { return { ok: false, message: 'El servidor entregó una respuesta inesperada.' } }
}

async function request(path: string, token = '', init: RequestInit = {}): Promise<ResponseData> {
  const headers = new Headers(init.headers)
  headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)
  const response = await fetch(path, { ...init, headers })
  const data = await parse(response)
  if (!response.ok || !data.ok) throw new Error(data.message || 'No fue posible completar la acción.')
  return data
}

export async function login(password: string): Promise<string> {
  const data = await request('/api/login', '', { method: 'POST', body: JSON.stringify({ password }) })
  if (!data.token) throw new Error('El servidor no entregó una sesión válida.')
  return data.token
}

export async function loadData(token: string): Promise<DataBundle> {
  const data = await request('/api/data', token)
  return {
    students: data.students ?? [],
    sessions: data.sessions ?? [],
    files: data.files ?? {},
    history: data.history ?? [],
    images: data.images ?? [],
    metadata: data.metadata ?? [],
  }
}

export async function saveConfig(token: string, kind: 'student' | 'session', item: Row): Promise<ResponseData> {
  return request('/api/save-config', token, { method: 'POST', body: JSON.stringify({ kind, item }) })
}

export async function deleteConfig(token: string, kind: 'student' | 'session', id: string): Promise<ResponseData> {
  return request('/api/delete-config', token, { method: 'POST', body: JSON.stringify({ kind, id }) })
}

export async function saveContent(token: string, fileName: string, csvText: string): Promise<ResponseData> {
  return request('/api/save-content', token, { method: 'POST', body: JSON.stringify({ fileName, csvText }) })
}

export async function saveHistoryRemote(token: string, entry: HistoryEntry): Promise<void> {
  await request('/api/meta', token, { method: 'POST', body: JSON.stringify({ kind: 'history', item: entry }) })
}

export async function saveImageRemote(token: string, image: ImageRecord): Promise<void> {
  await request('/api/meta', token, { method: 'POST', body: JSON.stringify({ kind: 'image', item: image }) })
}

export async function githubStatus(token: string): Promise<boolean> {
  const data = await request('/api/github-status', token)
  return Boolean(data.configured)
}

export interface GitHubUploadItem { path: string; contentBase64: string }
export async function uploadGitHubBatch(token: string, items: GitHubUploadItem[], message: string): Promise<{ commitSha?: string; commitUrl?: string }> {
  const data = await request('/api/github-upload', token, { method: 'POST', body: JSON.stringify({ items, message }) })
  return { commitSha: data.commitSha, commitUrl: data.commitUrl }
}
